import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User as UserIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const DRIVER_EMPTY = {
  first_name: "", last_name: "",
  vehicle_model: "", vehicle_year: "", evtc_number: "", plate_number: "",
  company_name: "", company_address: "", siret: "",
  vtc_card_number: "", vtc_card_expiry: "",
  cct_number: "", cct_expiry: "",
  insurance_company: "", insurance_policy: "", insurance_expiry: "",
  vat_status: "",
};

const DRIVER_MISSING_LABELS = {
  first_name: "Prénom", last_name: "Nom", phone: "Téléphone",
  vehicle_model: "Modèle véhicule", plate_number: "Immatriculation",
  evtc_number: "N° EVTC", vtc_card_number: "Carte pro VTC",
  company_name: "Société", siret: "SIRET", insurance_company: "Assurance",
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useI18n();

  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    current_password: "", password: "",
    ...DRIVER_EMPTY,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm((p) => {
      const next = { ...p, name: user.name || "", email: user.email || "", phone: user.phone || "" };
      if (user.role === "driver") {
        for (const k of Object.keys(DRIVER_EMPTY)) next[k] = user[k] || "";
      }
      return next;
    });
  }, [user]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { name: form.name, phone: form.phone };
      if (form.email && form.email.toLowerCase() !== (user?.email || "").toLowerCase()) payload.email = form.email;
      if (form.password) payload.password = form.password;
      if (payload.email || payload.password) payload.current_password = form.current_password;
      if (user?.role === "driver") {
        for (const k of Object.keys(DRIVER_EMPTY)) payload[k] = form[k] || "";
      }
      const { data } = await api.put("/auth/profile", payload);
      setUser?.(data);
      toast.success(t("profile.saved"));
      setForm((p) => ({ ...p, current_password: "", password: "" }));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  const back = () => {
    if (user?.role === "admin") navigate("/admin");
    else if (user?.role === "agency") navigate("/agency");
    else navigate("/driver");
  };

  if (!user) return null;

  return (
    <div data-testid="profile-page" className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#050505]/80 border-b border-zinc-800/50">
        <div className="max-w-xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <Button data-testid="profile-back-btn" variant="ghost" size="sm" onClick={back} className="text-zinc-400 hover:text-white hover:bg-zinc-800">
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-lg font-medium text-white flex items-center gap-2">
            <UserIcon size={20} className="text-amber-500" /> {t("profile.title")}
          </h1>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <form onSubmit={save} className="space-y-4 bg-[#121212] border border-zinc-800 rounded-lg p-5" data-testid="profile-form">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("profile.name")}</Label>
            <Input data-testid="profile-name" value={form.name} onChange={set("name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("profile.email")} *</Label>
            <Input data-testid="profile-email" type="email" value={form.email} onChange={set("email")} className="bg-[#1A1A1A] border-zinc-700 text-white h-11" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("profile.phone")}</Label>
            <Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} className="bg-[#1A1A1A] border-zinc-700 text-white h-11" placeholder="+33 ..." />
          </div>

          {user.role === "driver" && (
            <div className="pt-3 border-t border-zinc-800 space-y-3">
              {user.profile_status && !user.profile_status.complete && (
                <p data-testid="driver-profile-incomplete" className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                  Profil à compléter : {(user.profile_status.missing || []).map(k => DRIVER_MISSING_LABELS[k] || k).join(", ")}. Ces informations apparaîtront sur vos bons de transport.
                </p>
              )}
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider">État civil</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Prénom</Label><Input data-testid="profile-first-name" value={form.first_name} onChange={set("first_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Nom</Label><Input data-testid="profile-last-name" value={form.last_name} onChange={set("last_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>
              <p className="text-[11px] text-zinc-500 uppercase tracking-wider pt-1">Véhicule</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Modèle</Label><Input value={form.vehicle_model} onChange={set("vehicle_model")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Année</Label><Input value={form.vehicle_year} onChange={set("vehicle_year")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Immatriculation</Label><Input value={form.plate_number} onChange={set("plate_number")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>

              <p className="text-[11px] text-zinc-500 uppercase tracking-wider pt-1">Société</p>
              <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Nom société</Label><Input value={form.company_name} onChange={set("company_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Adresse société</Label><Input value={form.company_address} onChange={set("company_address")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">SIRET</Label><Input value={form.siret} onChange={set("siret")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">N° EVTC</Label><Input value={form.evtc_number} onChange={set("evtc_number")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-400 uppercase tracking-wider">Statut TVA *</Label>
                <p className="text-[11px] text-zinc-500">Indique automatiquement la mention légale sur vos factures.</p>
                <select
                  data-testid="profile-vat-status"
                  value={form.vat_status}
                  onChange={set("vat_status")}
                  className="w-full bg-[#1A1A1A] border border-zinc-700 text-white h-10 px-3 rounded-md focus:border-amber-500 focus:outline-none"
                >
                  <option value="">— Sélectionner —</option>
                  <option value="non_assujetti">Non assujetti — franchise en base (art. 293 B du CGI)</option>
                  <option value="assujetti">Assujetti à la TVA</option>
                </select>
              </div>

              <p className="text-[11px] text-zinc-500 uppercase tracking-wider pt-1">Documents pro</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Carte pro VTC</Label><Input value={form.vtc_card_number} onChange={set("vtc_card_number")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Expir. VTC</Label><Input type="date" value={form.vtc_card_expiry} onChange={set("vtc_card_expiry")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">CCT</Label><Input value={form.cct_number} onChange={set("cct_number")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Expir. CCT</Label><Input type="date" value={form.cct_expiry} onChange={set("cct_expiry")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Assurance — compagnie</Label><Input value={form.insurance_company} onChange={set("insurance_company")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">N° police</Label><Input value={form.insurance_policy} onChange={set("insurance_policy")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-zinc-400 uppercase tracking-wider">Expir. assurance</Label><Input type="date" value={form.insurance_expiry} onChange={set("insurance_expiry")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" /></div>
              </div>
            </div>
          )}
          <div className="pt-2 border-t border-zinc-800 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("profile.current_password")}</Label>
              <Input data-testid="profile-current-pwd" type="password" value={form.current_password} onChange={set("current_password")} className="bg-[#1A1A1A] border-zinc-700 text-white h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">{t("profile.new_password")}</Label>
              <Input data-testid="profile-new-pwd" type="password" value={form.password} onChange={set("password")} className="bg-[#1A1A1A] border-zinc-700 text-white h-11" placeholder="—" />
            </div>
          </div>
          <Button data-testid="profile-save-btn" type="submit" disabled={loading} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-11">
            {loading ? "..." : t("profile.save")}
          </Button>
        </form>
      </main>
    </div>
  );
}
